import React from 'react';
import { graphql } from 'gatsby';

import { useFelaEnhanced, type RulesExtend } from '@ackee/fela';

import { Title, LocalizedLink, TitleTypes } from 'modules/ui';
import { Language, languageSpecificConfig } from 'config/language';

import type { BlogPost } from '../../types';
import { BlogPostMeta } from '../BlogPostMeta';

import * as felaRules from './BlogPostCard.rules';

export interface BlogPostCardProps extends BlogPost {
    extend: RulesExtend<typeof felaRules>;
    languageMutation: undefined | Language;
}

export const BlogPostCard = ({
    slug,
    title,
    thumbnail,
    author,
    categories,
    publishedAt,
    extend,
    languageMutation = undefined,
}: BlogPostCardProps) => {
    const { styles, rules } = useFelaEnhanced(felaRules, { extend });

    return (
        <article className={styles.post}>
            {!!languageMutation ? (
                <a href={languageSpecificConfig[languageMutation].SITE_URL + '/blog/' + slug} className={styles.link}>
                    {thumbnail && (
                        <div className={styles.thumbnailWrapper}>
                            <img
                                src={thumbnail.formats.medium?.url ?? thumbnail.url}
                                alt={''}
                                className={`${styles.thumbnail} thumbnail`}
                            />
                            <div className={`${styles.thumbnailMask} mask`} />
                        </div>
                    )}
                    {/** @ts-expect-error */}
                    <Title type={TitleTypes.H4} extend={{ title: rules.title }}>
                        {title}
                    </Title>
                </a>
            ) : (
                <LocalizedLink to={`/blog/${slug}`} extend={{ link: rules.link }}>
                    {thumbnail && (
                        <div className={styles.thumbnailWrapper}>
                            <img
                                src={thumbnail.formats.medium?.url ?? thumbnail.url}
                                alt={''}
                                className={`${styles.thumbnail} thumbnail`}
                            />
                            <div className={`${styles.thumbnailMask} mask`} />
                        </div>
                    )}
                    {/** @ts-expect-error */}
                    <Title type={TitleTypes.H4} extend={{ title: rules.title }}>
                        {title}
                    </Title>
                </LocalizedLink>
            )}
            <BlogPostMeta
                author={author}
                categories={categories}
                publishedAt={publishedAt}
                languageMutation={languageMutation}
            />
        </article>
    );
};

export const query = graphql`
    fragment BlogPost on STRAPI_POST {
        id
        slug
        title
        author {
            ...PostAuthor
        }
        categories {
            ...PostCategory
        }
        thumbnail {
            url
            width
            height
            formats {
                medium {
                    url
                    width
                    height
                }
            }
        }
        publishedAt
    }
`;
